import React, { useEffect, useMemo, useState } from 'react';
import { Activity, Check, Download, Eye, EyeOff, FolderSearch, HardDrive, Loader2, Search, X } from 'lucide-react';
import InfoTip from './InfoTip';
import { formatBytes, useNas } from './NasProvider';
import NasScanPanel from './NasScanPanel';

const normalize = (value = '') => String(value).toLocaleLowerCase('vi-VN').normalize('NFD').replace(/\p{M}/gu, '').replace(/đ/g, 'd');

function DiagnosePanel({ onClose }) {
    const { callApi } = useNas();
    const [steps, setSteps] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        callApi('diagnose').then(data => setSteps(data.steps || [])).catch(err => setError(err.message));
    }, [callApi]);

    return (
        <div className="nas-diagnose">
            <div className="nas-diagnose-head">
                <strong>Kiểm tra kết nối NAS</strong>
                <button type="button" className="nas-icon-btn" onClick={onClose} aria-label="Đóng"><X size={16} /></button>
            </div>
            {!steps && !error && <p className="nas-muted"><Loader2 size={14} className="animate-spin" /> Đang kiểm tra…</p>}
            {error && <p className="nas-error">{error}</p>}
            {steps && <ul>
                {steps.map(step => (
                    <li key={step.label} className={step.ok ? 'is-ok' : 'is-bad'}>
                        {step.ok ? <Check size={14} /> : <X size={14} />}
                        <span><b>{step.label}</b>{step.detail && <small>{step.detail}</small>}</span>
                    </li>
                ))}
            </ul>}
        </div>
    );
}

export default function NasView({ userRole, initialQuery = '' }) {
    const nas = useNas();
    const isAdmin = userRole === 'admin';
    const [query, setQuery] = useState(initialQuery);
    const [genre, setGenre] = useState('all');
    const [showDiagnose, setShowDiagnose] = useState(false);
    const [showScan, setShowScan] = useState(false);
    const [busyId, setBusyId] = useState('');

    useEffect(() => { setQuery(initialQuery); }, [initialQuery]);

    const visible = useMemo(() => (nas?.items || []).filter(item => isAdmin || !item.quarantined), [nas, isAdmin]);
    const genres = useMemo(() => [...new Set(visible.map(item => item.genre))].sort((a, b) => a.localeCompare(b, 'vi')), [visible]);
    const filtered = useMemo(() => {
        const needle = normalize(query.trim());
        return visible.filter(item => (genre === 'all' || item.genre === genre)
            && (!needle || normalize(`${item.work} ${item.author} ${item.genre} ${(item.keys || []).join(' ')}`).includes(needle)));
    }, [visible, query, genre]);
    const totalBytes = visible.reduce((sum, item) => sum + (item.quarantined ? 0 : item.bytes || 0), 0);

    if (!nas) return null;

    const toggleQuarantine = async item => {
        const action = item.quarantined ? 'restore' : 'quarantine';
        if (action === 'quarantine' && !window.confirm(`Ẩn "${item.work}" khỏi kho NAS? Nhân viên sẽ không tải được nữa (file vẫn còn trên NAS).`)) return;
        setBusyId(item.id);
        try { await nas.callApi(action, { archiveId: item.id }); } catch (error) { window.alert(error.message); } finally { setBusyId(''); }
    };

    return (
        <div className="nas-page">
            <header className="nas-page-head">
                <div className="nas-page-title">
                    <span className="nas-page-icon"><HardDrive size={20} /></span>
                    <div>
                        <h2>Kho NAS
                            <InfoTip>
                                Nơi lưu file audio của các task đã làm. Trước khi tải torrent, hãy tìm sách ở đây: có rồi thì bấm <b>Tải về</b> cho nhanh.
                                Chưa có thì tải torrent như bình thường, xong việc bấm <b>Lưu NAS</b> ở task để cả team dùng lại sau này.
                                Sách có bản quyền không được lưu vào kho.
                            </InfoTip>
                        </h2>
                        <p>{visible.filter(item => !item.quarantined).length} tác phẩm · {formatBytes(totalBytes)}</p>
                    </div>
                </div>
                {isAdmin && (
                    <div className="nas-admin-actions">
                        <button type="button" className="nas-btn is-ghost" onClick={() => setShowScan(value => !value)}><FolderSearch size={15} /> Quét dữ liệu NAS cũ</button>
                        <button type="button" className="nas-btn is-ghost" onClick={() => setShowDiagnose(value => !value)}><Activity size={15} /> Kiểm tra kết nối NAS</button>
                    </div>
                )}
            </header>

            {showScan && <NasScanPanel onClose={() => setShowScan(false)} />}
            {showDiagnose && <DiagnosePanel onClose={() => setShowDiagnose(false)} />}

            <div className="nas-toolbar">
                <label className="nas-search">
                    <Search size={16} />
                    <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Tìm tên sách, series, tác giả…" autoFocus />
                </label>
                <select value={genre} onChange={event => setGenre(event.target.value)} aria-label="Lọc thể loại">
                    <option value="all">Tất cả thể loại</option>
                    {genres.map(value => <option key={value} value={value}>{value}</option>)}
                </select>
            </div>

            {!nas.loaded ? (
                <p className="nas-muted nas-empty"><Loader2 size={16} className="animate-spin" /> Đang tải…</p>
            ) : filtered.length === 0 ? (
                <div className="nas-empty">
                    <HardDrive size={28} />
                    <strong>{visible.length ? 'Không tìm thấy sách phù hợp' : 'Kho NAS chưa có dữ liệu'}</strong>
                    <p>{visible.length ? 'Sách này chưa có trên NAS, hãy tải bằng torrent.' : 'Vào Quản lý Task, bấm “Lưu NAS” ở task đã tải xong để bắt đầu.'}</p>
                </div>
            ) : (
                <ul className="nas-list">
                    {filtered.map(item => (
                        <li key={item.id} className={`nas-item ${item.quarantined ? 'is-hidden' : ''}`}>
                            <div className="nas-item-main">
                                <strong title={item.work}>{item.work}</strong>
                                <span>{item.author} · {item.genre}</span>
                            </div>
                            <div className="nas-item-meta">
                                <span>{item.audio} file</span>
                                <span>{formatBytes(item.bytes)}</span>
                            </div>
                            <div className="nas-item-actions">
                                {item.quarantined
                                    ? <span className="nas-tag">Đang ẩn</span>
                                    : <button type="button" className="nas-btn is-primary is-small" onClick={() => nas.openDownload(item.id)}><Download size={14} /> Tải về</button>}
                                {isAdmin && (
                                    <button type="button" className="nas-icon-btn" onClick={() => toggleQuarantine(item)} disabled={busyId === item.id} title={item.quarantined ? 'Khôi phục' : 'Ẩn khỏi kho'} aria-label={item.quarantined ? 'Khôi phục' : 'Ẩn khỏi kho'}>
                                        {busyId === item.id ? <Loader2 size={15} className="animate-spin" /> : item.quarantined ? <Eye size={15} /> : <EyeOff size={15} />}
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
